import './radio-group-buttons.scss';

import React, { PropsWithChildren, useEffect } from 'react';
import { useRadio, useRadioGroup } from 'react-aria';
import { type RadioGroupState, useRadioGroupState } from 'react-stately';

import { cn } from '../../utils/helpers';

const RadioContext = React.createContext<RadioGroupState | null>(null);

type RadioGroupButtonsProps<TValue> = PropsWithChildren & {
  errorMessage?: string;
  label?: string;
  defaultValue: TValue;
  onChange: (selectedValue: TValue) => void;
};

// Looks like VSCode has some issues with an arrow function here without adding some verbose type assertions.
// eslint-disable-next-line react/function-component-definition, prefer-arrow-functions/prefer-arrow-functions
export function RadioGroupButtons<TValue extends string = string>(
  props: RadioGroupButtonsProps<TValue>,
) {
  const { onChange, ...rest } = props;
  const { children, errorMessage, label } = rest;
  const state = useRadioGroupState(rest);
  const { radioGroupProps, labelProps, errorMessageProps } = useRadioGroup(
    rest,
    state,
  );

  const { selectedValue } = state;
  useEffect(() => {
    onChange(selectedValue as TValue);
  }, [selectedValue]);

  return (
    <div {...radioGroupProps} className="RadioGroupButtons">
      {label && (
        <p className="RadioGroupButtons__label" {...labelProps}>
          {label}
        </p>
      )}
      <RadioContext.Provider value={state}>
        <div className="RadioGroupButtons__list gap-comfy-xs my-comfy-lg">
          {children}
        </div>
      </RadioContext.Provider>
      {errorMessage && state.isInvalid && (
        <div {...errorMessageProps} style={{ color: 'red', fontSize: 12 }}>
          {errorMessage}
        </div>
      )}
    </div>
  );
}

export const RadioGroupButtonsItem = (
  props: PropsWithChildren<{ value: string; id: string }>,
) => {
  const { children, id } = props;
  const state = React.useContext(RadioContext) as RadioGroupState;
  const ref = React.useRef(null);
  const { inputProps, isSelected, labelProps } = useRadio(props, state, ref);

  return (
    <label
      {...labelProps}
      className={cn([
        'RadioGroupButtons__item relative flex justify-between',
        {
          'RadioGroupButtons__item--checked': isSelected,
        },
      ])}
      htmlFor={id}
    >
      <input {...inputProps} ref={ref} id={id} className="sr-only" />
      {children}
      <div
        className="RadioGroupButtons__indicator absolute top-comfy-xs right-comfy-xs"
        aria-hidden="true"
      />
    </label>
  );
};
